import { useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';

interface OtpInputProps {
  value: string;
  onChange: (next: string) => void;
  /** Number of digits expected — 6 for Supabase phone MFA. */
  length?: number;
  disabled?: boolean;
  /** Auto-focus the first empty cell on mount. Defaults to true. */
  autoFocus?: boolean;
  /** Fired once the user has entered `length` digits. */
  onComplete?: (code: string) => void;
  className?: string;
}

/**
 * Six-cell numeric OTP input. Each cell is a separate `<input>` so we get
 * the iOS / Android "from messages" autofill pop-up for free, and so
 * paste-spreads ("paste 123456 into cell 1") populate every cell.
 *
 * The component is uncontrolled per-cell but controlled overall: the
 * canonical state is the parent's `value` string, the cells just mirror it.
 */
export function OtpInput({
  value,
  onChange,
  length = 6,
  disabled,
  autoFocus = true,
  onComplete,
  className,
}: OtpInputProps) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (!autoFocus) return;
    const firstEmpty = Math.min(value.length, length - 1);
    refs.current[firstEmpty]?.focus();
  }, [autoFocus, length, value.length]);

  useEffect(() => {
    if (value.length === length) onComplete?.(value);
  }, [value, length, onComplete]);

  const setDigit = (idx: number, digit: string) => {
    const next = value.slice(0, idx) + digit + value.slice(idx + 1);
    onChange(next.slice(0, length));
  };

  const handleChange = (idx: number, raw: string) => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setDigit(idx, '');
      return;
    }
    if (digits.length > 1) {
      const merged = (value.slice(0, idx) + digits).slice(0, length);
      onChange(merged);
      const focusTo = Math.min(merged.length, length - 1);
      refs.current[focusTo]?.focus();
      return;
    }
    setDigit(idx, digits);
    if (idx < length - 1) refs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !value[idx] && idx > 0) {
      e.preventDefault();
      setDigit(idx - 1, '');
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowLeft' && idx > 0) {
      e.preventDefault();
      refs.current[idx - 1]?.focus();
    } else if (e.key === 'ArrowRight' && idx < length - 1) {
      e.preventDefault();
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    const focusTo = Math.min(text.length, length - 1);
    refs.current[focusTo]?.focus();
  };

  return (
    <div
      role="group"
      aria-label="One-time verification code"
      className={cn('flex items-center gap-2', className)}
    >
      {Array.from({ length }).map((_, idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          autoComplete={idx === 0 ? 'one-time-code' : 'off'}
          maxLength={length}
          pattern="[0-9]*"
          value={value[idx] ?? ''}
          disabled={disabled}
          onChange={(e) => handleChange(idx, e.target.value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          aria-label={`Digit ${idx + 1} of ${length}`}
          className={cn(
            'h-12 w-10 rounded-lg border border-input bg-card text-center text-lg font-medium tracking-widest text-foreground transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
        />
      ))}
    </div>
  );
}
